import { getDocs, collection, query, where, updateDoc, doc, getDoc, onSnapshot } from "firebase/firestore";
import { db, auth } from '../../../../firebase/config';
import { fetchBranchBankId } from '../../../branches/service/branchesService';

export type NewLocationSurvey = {
    id: string;
    surveyId: string;
    createdBy: string;
    createdByName?: string;
    locationAddress: string;
    surveyRequestDate: string;
    surveyDate: string;
    surveySubmittedOn: string;
    spaceConsistsOf?: string;
    hazardousArea?: string;
    directions?: Record<string, any>;
    questions?: Record<string, any>;
    remarks?: string;
    createdAt?: any;
    assignedTo?: string;
    assignedToName?: string;
    assignedToCode?: string;
    assignedBy?: string;
    assignedByName?: string;
    bankId?: string | null;
    status?: string;
    isHazardous?: boolean;
    cancellationReason?: string;
    completedBy?: string;
    completedAt?: any;
};

type SurveysMap = Map<string, NewLocationSurvey>;

const sortSurveys = (surveys: NewLocationSurvey[]) => {
    return surveys.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
        return dateB.getTime() - dateA.getTime();
    });
};

export function subscribeToNewLocationSurveys(onUpdate: (surveys: NewLocationSurvey[]) => void): () => void {
    const uid = auth.currentUser?.uid;
    if (!uid) {
        console.log('No user ID found');
        return () => {};
    }

    let disposed = false;
    const unsubscribers: Array<() => void> = [];
    const surveysMap: SurveysMap = new Map();

    const emit = () => {
        if (disposed) return;
        onUpdate(sortSurveys(Array.from(surveysMap.values())));
    };

    const attachListener = (queryRef: any) => {
        const unsubscribe = onSnapshot(queryRef, (snapshot: any) => {
            snapshot.docChanges().forEach((change: any) => {
                const data = change.doc.data();

                if (change.type === 'removed') {
                    surveysMap.delete(change.doc.id);
                } else {
                    const questions = data.questions || {};
                    const hazardFromQuestions = questions.fuelStationAvailable === 'Yes' || questions.otherAssessmentHazardous === 'Yes';
                    const hazardFromDetails = typeof data.hazardousArea === 'string' && data.hazardousArea.trim() !== '';
                    const computedHazard = data.isHazardous ?? (hazardFromQuestions || hazardFromDetails);
                    surveysMap.set(change.doc.id, {
                        id: change.doc.id,
                        ...data,
                        isHazardous: computedHazard
                    } as NewLocationSurvey);
                }
            });

            emit();
        }, (error: any) => {
            console.error('Error listening to surveys:', error);
        });

        unsubscribers.push(unsubscribe);
    };

    (async () => {
        let bankId: string | null = null;
        try {
            bankId = await fetchBranchBankId(uid);
        } catch (error) {
            console.error('Error resolving bank ID for surveys:', error);
        }

        if (!bankId) {
            console.warn('Bank ID not found for user. Location survey subscription aborted.');
            return;
        }

        let designation = '';
        try {
            const userDoc = await getDoc(doc(db, 'banks', bankId, 'pos', uid));
            if (userDoc.exists()) {
                designation = (userDoc.data()?.designation || '').toString();
            }
        } catch (error) {
            console.error('Error fetching user designation for surveys:', error);
        }

        const normalizedDesignation = designation.replace(/_/g, ' ').toLowerCase();
        const isMIS = normalizedDesignation.includes('mis');
        const isRegional = normalizedDesignation.includes('regional pso');

        const surveysRef = collection(db, 'banks', bankId, 'locationSurveys');

        if (isMIS) {
            attachListener(surveysRef);
        } else {
            attachListener(query(surveysRef, where('createdBy', '==', uid)));
            attachListener(query(surveysRef, where('assignedTo', '==', uid)));

            if (isRegional && bankId) {
                try {
                    const managedPsosSnap = await getDocs(collection(db, 'banks', bankId, 'pos', uid, 'managedPsos'));
                    const managedIds = managedPsosSnap.docs
                        .map(docSnap => docSnap.id || (docSnap.data()?.psoId as string | undefined))
                        .filter((id): id is string => Boolean(id));

                    managedIds.forEach((managedId) => {
                        attachListener(query(surveysRef, where('createdBy', '==', managedId)));
                        attachListener(query(surveysRef, where('assignedTo', '==', managedId)));
                    });
                } catch (error) {
                    console.error('Error fetching managed PSO surveys:', error);
                }
            }
        }
    })();

    return () => {
        disposed = true;
        unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
}

export async function fetchNewLocationSurveys(): Promise<NewLocationSurvey[]> {
    const uid = auth.currentUser?.uid;
    if (!uid) {
        console.log('No user ID found');
        return [];
    }

    try {
        const surveysMap = new Map<string, NewLocationSurvey>();

        const addSnapshotToMap = (snapshot: any, bankFilter?: string | null) => {
            snapshot.forEach((docSnap: any) => {
                const data = docSnap.data();
                const docBankId = data.bankId ?? null;

                if (bankFilter && docBankId && docBankId !== bankFilter) {
                    return;
                }

                const questions = data.questions || {};
                const hazardFromQuestions = questions.fuelStationAvailable === 'Yes' || questions.otherAssessmentHazardous === 'Yes';
                const hazardFromDetails = typeof data.hazardousArea === 'string' && data.hazardousArea.trim() !== '';
                const computedHazard = data.isHazardous ?? (hazardFromQuestions || hazardFromDetails);

                surveysMap.set(docSnap.id, {
                    id: docSnap.id,
                    ...data,
                    isHazardous: computedHazard
                } as NewLocationSurvey);
            });
        };

        let bankId: string | null = null;
        try {
            bankId = await fetchBranchBankId(uid);
        } catch (error) {
            console.error('Error resolving bank ID for surveys:', error);
        }

        if (!bankId) {
            console.warn('Bank ID not found for user. Cannot fetch location surveys.');
            return [];
        }

        const surveysRef = collection(db, 'banks', bankId, 'locationSurveys');

        let designation = '';
        try {
            const userDoc = await getDoc(doc(db, 'banks', bankId, 'pos', uid));
            if (userDoc.exists()) {
                designation = (userDoc.data()?.designation || '').toString();
            }
        } catch (error) {
            console.error('Error fetching user designation for surveys:', error);
        }

        const normalizedDesignation = designation.replace(/_/g, ' ').toLowerCase();
        const isMIS = normalizedDesignation.includes('mis');
        const isRegional = normalizedDesignation.includes('regional pso');

        if (isMIS) {
            const allSnap = await getDocs(surveysRef);
            addSnapshotToMap(allSnap, bankId);
        } else {
            const createdSnap = await getDocs(query(surveysRef, where('createdBy', '==', uid)));
            addSnapshotToMap(createdSnap);

            const assignedSnap = await getDocs(query(surveysRef, where('assignedTo', '==', uid)));
            addSnapshotToMap(assignedSnap);

            if (isRegional && bankId) {
                try {
                    const managedPsosSnap = await getDocs(collection(db, 'banks', bankId, 'pos', uid, 'managedPsos'));
                    const managedIds = managedPsosSnap.docs
                        .map(docSnap => docSnap.id || (docSnap.data()?.psoId as string | undefined))
                        .filter((id): id is string => Boolean(id));

                    for (const managedId of managedIds) {
                        const managedCreated = await getDocs(query(surveysRef, where('createdBy', '==', managedId)));
                        addSnapshotToMap(managedCreated);

                        const managedAssigned = await getDocs(query(surveysRef, where('assignedTo', '==', managedId)));
                        addSnapshotToMap(managedAssigned);
                    }
                } catch (error) {
                    console.error('Error fetching managed PSO surveys:', error);
                }
            }
        }

        const surveysList: NewLocationSurvey[] = Array.from(surveysMap.values());

        console.log('Fetched New Location Surveys:', surveysList.length);

        return sortSurveys(surveysList);
    } catch (error) {
        console.error('Error fetching location surveys:', error);
        return [];
    }
}

export async function updateNewLocationSurvey(surveyId: string, bankId: string | null | undefined, updates: Partial<NewLocationSurvey>): Promise<void> {
    const uid = auth.currentUser?.uid;
    if (!uid) {
        throw new Error('User not authenticated');
    }

    if (!bankId) {
        throw new Error('Bank ID is required to update the survey');
    }

    try {
        const surveyRef = doc(db, 'banks', bankId, 'locationSurveys', surveyId);
        await updateDoc(surveyRef, {
            ...updates,
            updatedAt: new Date()
        });
    } catch (error) {
        console.error('Error updating location survey:', error);
        throw error;
    }
}

